// 「所作の記録」= キー設定の表 (2026-10-31 作者「キー設定はブラウザ版に先に」「コントローラーも対応して」)。
//   タイトルと幕間の両方から開く。 座標は描画 (render.ts drawRecordsPanel) と当たり判定 (game.ts) の共通の出所。
//   行: 移動(左) / 移動(右) / 攻撃 / パリィ / 回避 = 割り当てを変えられる。 幕間・決定と戻る = 固定 (表示だけ)。
//   その下に「既定に戻す」「閉じる」。

import { ARENA } from './balance'
import type { KbmBindAction, PadBindAction } from './key_bindings'
import type { MenuRect } from './menu_layout'

export type ControlsRow = { label: string, kbm: KbmBindAction, pad: PadBindAction | null }

export const CONTROLS_ROWS: readonly ControlsRow[] = [
  { label: '移動（左）', kbm: 'left', pad: null },
  { label: '移動（右）', kbm: 'right', pad: null },
  { label: '攻撃', kbm: 'attack', pad: 'attack' },
  { label: 'パリィ（長押しでガード）', kbm: 'parry', pad: 'parry' },
  { label: '回避', kbm: 'step', pad: 'step' },
]

// カーソルの並び: 0-4 = 上の行 / 5 = 既定に戻す / 6 = 閉じる。
export const CONTROLS_RESET_INDEX = CONTROLS_ROWS.length
export const CONTROLS_CLOSE_INDEX = CONTROLS_ROWS.length + 1
export const CONTROLS_ITEM_COUNT = CONTROLS_ROWS.length + 2

export const CONTROLS_PANEL = {
  w: 620,
  h: 372,
  y: 70,
  headerY: 66,     // 列の見出しの文字の下端 (パネル上端から)
  firstRowY: 80,   // 最初の行の上端
  rowH: 32,
  fixedRowH: 24,   // 固定の行 (幕間・決定と戻る)
  labelX: 28,      // 所作の名前の左端
  kbmX: 262, padX: 444, cellW: 156, cellH: 26,
  buttonW: 150, buttonH: 30, buttonGap: 24,
} as const

export function controlsPanelX(): number { return ARENA.width / 2 - CONTROLS_PANEL.w / 2 }

export function controlsRowTop(row: number): number {
  return CONTROLS_PANEL.y + CONTROLS_PANEL.firstRowY + row * CONTROLS_PANEL.rowH
}

export function controlsFixedRowTop(i: number): number {
  return controlsRowTop(CONTROLS_ROWS.length) + 6 + i * CONTROLS_PANEL.fixedRowH
}

export function controlsButtonsTop(): number {
  return controlsFixedRowTop(2) + 14
}

export function controlsCellRect(row: number, col: 'kbm' | 'pad'): MenuRect {
  const px = controlsPanelX()
  const x = px + (col === 'kbm' ? CONTROLS_PANEL.kbmX : CONTROLS_PANEL.padX)
  const y = controlsRowTop(row) + (CONTROLS_PANEL.rowH - CONTROLS_PANEL.cellH) / 2
  return { x, y, w: CONTROLS_PANEL.cellW, h: CONTROLS_PANEL.cellH }
}

export function controlsButtonRect(which: 'reset' | 'close'): MenuRect {
  const cx = ARENA.width / 2
  const { buttonW: w, buttonH: h, buttonGap: gap } = CONTROLS_PANEL
  const x = which === 'reset' ? cx - gap / 2 - w : cx + gap / 2
  return { x, y: controlsButtonsTop(), w, h }
}

// ホバー用: カーソルの並びごとの矩形 (行は全幅)。
export function controlsItemRects(): MenuRect[] {
  const px = controlsPanelX()
  const rows = CONTROLS_ROWS.map((_, i) => ({ x: px + 12, y: controlsRowTop(i), w: CONTROLS_PANEL.w - 24, h: CONTROLS_PANEL.rowH }))
  return [...rows, controlsButtonRect('reset'), controlsButtonRect('close')]
}

export type ControlsHit =
  | { kind: 'cell', row: number, col: 'kbm' | 'pad' }
  | { kind: 'reset' }
  | { kind: 'close' }
  | null

function inRect(r: MenuRect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h
}

// クリックした所。 パッドの列は移動の行だけ欄が無い (左スティック・十字キーで固定)。
export function controlsHitAt(x: number, y: number): ControlsHit {
  for (let row = 0; row < CONTROLS_ROWS.length; row += 1) {
    if (inRect(controlsCellRect(row, 'kbm'), x, y)) return { kind: 'cell', row, col: 'kbm' }
    if (CONTROLS_ROWS[row]!.pad && inRect(controlsCellRect(row, 'pad'), x, y)) return { kind: 'cell', row, col: 'pad' }
  }
  if (inRect(controlsButtonRect('reset'), x, y)) return { kind: 'reset' }
  if (inRect(controlsButtonRect('close'), x, y)) return { kind: 'close' }
  return null
}

export function controlsInsidePanel(x: number, y: number): boolean {
  return inRect({ x: controlsPanelX(), y: CONTROLS_PANEL.y, w: CONTROLS_PANEL.w, h: CONTROLS_PANEL.h }, x, y)
}
